import React, { ReactElement } from 'react';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { useTranslation } from 'react-i18next';
import ReactPlaceholder from 'react-placeholder';
import { useSelector } from 'react-redux';
import { useGetThresholdsQuery } from 'business-dashboard/src/api/thresholdsApi';
import { mediumFormLoader } from 'commons/src/components/placeholder';
import ResponseBox from 'commons/src/components/responseMessages/ResponseBox';
import { sensorTypesWithCustomThresholds } from 'commons/src/DeviceAndSensorLists';
import { SensorTypes, ThresholdsResourceScope } from 'commons/src/models/commonEnums';
import { SpaceThresholds } from 'commons/src/models/customThresholdModels';
import { Store } from 'commons/src/reducers';
import { toErrorType } from 'commons/src/sagas/isErrorType';
import DeviceSensorThresholdForm from './DeviceSensorThresholdForm';

export type DeviceThresholdsContentProps = {
    serialNumber: string;
    /** When true, sliders and save are disabled (e.g. inline expandable view in device settings) */
    readOnly?: boolean;
    /** When false, query is skipped (e.g. when expandable is closed or modal is closed) */
    active?: boolean;
    onUpdateSuccess?: () => void;
};

const DeviceThresholdsContent = ({
    serialNumber,
    readOnly = false,
    active = true,
    onUpdateSuccess,
}: DeviceThresholdsContentProps): ReactElement => {
    const { t: txt } = useTranslation();
    const { selectedGroup } = useSelector((state: Store) => state.userSettings);

    const {
        data: thresholdData,
        error,
        isSuccess,
    } = useGetThresholdsQuery(
        active && serialNumber && selectedGroup?.id
            ? { resourceId: serialNumber, scope: ThresholdsResourceScope.DEVICE }
            : skipToken,
        { refetchOnMountOrArgChange: true }
    );

    const fetchThresholdsError = error ? toErrorType(error) : undefined;

    const deviceSensorsWithThresholds = thresholdData
        ? [
              ...new Set(
                  sensorTypesWithCustomThresholds.filter(
                      sensor => (thresholdData as Record<string, SpaceThresholds>)[sensor]
                  )
              ),
          ]
        : [];

    if (fetchThresholdsError) {
        return <ResponseBox text={`ErrorCodes.${fetchThresholdsError.error}`} />;
    }

    return (
        <ReactPlaceholder ready={isSuccess} customPlaceholder={mediumFormLoader}>
            {isSuccess && deviceSensorsWithThresholds.length === 0 ? (
                <div>{txt('CustomThresholds.NoDeviceThresholdsConfigured')}</div>
            ) : (
                deviceSensorsWithThresholds.map(sensorType => (
                    <DeviceSensorThresholdForm
                        key={sensorType}
                        sensorType={sensorType as SensorTypes}
                        thresholds={(thresholdData as Record<string, SpaceThresholds>)[sensorType]}
                        serialNumber={serialNumber}
                        readOnly={readOnly}
                        onUpdateSuccess={readOnly ? undefined : onUpdateSuccess}
                    />
                ))
            )}
        </ReactPlaceholder>
    );
};

export default DeviceThresholdsContent;
